/**
 * Shared Pricing Scenarios Data
 *
 * This file contains all pricing test scenarios that can be used:
 * 1. Unit tests (pricingScenarios.test.js)
 * 2. Test Scenarios Dashboard (/test-scenarios)
 *
 * Structure:
 * - 46 templates, FMC restricted to UA → ~157 total scenarios
 * - 21 single-product + 25 multi-product templates
 */

import {
  calculatePhoneNumbersMonthlyTotal,
  calculatePhoneNumbersOneTimeTotal,
} from './pricingScenarioTotals';

// ===============================================
// PRICING CONSTANTS
// ===============================================

export const PACKAGE_TIER_PRICES = {
  basic: {
    ua: 11,      // $11/user (war discount from $15)
    eu: 20,      // €20/user
    other: 20,   // $20/user
    asia: 13.2,  // $13.2/user
  },
  pro: {
    ua: 22,      // $22/user
    eu: 34,      // €34/user
    other: 34,   // $34/user
    asia: 24,    // $24/user
  },
  corporate: {
    ua: 34,      // $34/user
    eu: 49,      // €49/user
    other: 49,   // $49/user
    asia: 37,    // $37/user
  },
  advanced: {
    ua: 22,      // $22/user (same as Pro)
    eu: 34,      // €34/user (same as Pro)
    other: 34,   // $34/user (same as Pro)
    asia: 24,    // $24/user (same as Pro)
  },
};

export const MIN_USERS = {
  basic: 3,
  pro: 3,
  corporate: 5,
  advanced: 5,
};

export const FMC_TIER_PRICES = {
  basic: 5,      // $5/user
  pro: 13,       // $13/user
  corporate: 19, // $19/user
  advanced: 13,  // $13/user (same as Pro)
};

export const FMC_MIN_USERS_STANDALONE = 6;

export const BASIC_TIER_ADDONS = {
  ai_conversation: {
    ua: 11,    // $11/user
    eu: 11,    // €11/user
    other: 11, // $11/user
    asia: 11,  // $11/user
  },
  omnichannel_chat: {
    ua: 7,     // $7/user
    eu: 10,    // €10/user
    other: 10, // $10/user
    asia: 7,   // $7/user
    minUsers: 3,
  },
  callback: {
    // Base prices BEFORE Ukraine discount (backend applies 0.7004 for UA)
    ua: 39 * 0.7004,  // $39 × 0.7004 = $27.32
    eu: 49,           // €49 flat
    other: 39,        // $39 flat
    asia: 39,         // $39 flat
  },
};

// Ukraine-specific discount coefficients
// Note: FMC does NOT have a discount coefficient - it's just users × price
export const UA_DISCOUNT_COEFFICIENTS = {
  callback: 0.7004,
  callTracking: {
    component1: 0.7004,
    component2: 0.711,
    variablePart: 0.715,
  },
};

// Bundle discount (for Call Tracking when BP is present)
export const BUNDLE_DISCOUNT_AMOUNT = {
  ua: 20,    // $20 for Ukraine
  eu: 29,    // €29 for Europe
  asia: 29,  // $29 for Asia
  other: 29, // $29 for Other
};

export const SUBSCRIPTION_360_BONUS = 0.20;    // 20% subscription bonus
export const SUBSCRIPTION_180_BONUS = 0.10;    // 10% subscription bonus

// Call Tracking base prices (not affected by package tier)
export const CALL_TRACKING_BASE = {
  component1: 39,  // $39
  component2: 20,  // $20
};

// VoiceBot plans (not affected by package tier, no subscription discount)
export const VOICEBOT_PLANS = [
  { name: 'lite',  price: 170, aiMinutes: 1100, roboMinutes: 11000, extraAI: 0.16, extraRobo: 0.016 },
  { name: 'pro',   price: 280, aiMinutes: 2000, roboMinutes: 20000, extraAI: 0.15, extraRobo: 0.015 },
  { name: 'max',   price: 520, aiMinutes: 4000, roboMinutes: 40000, extraAI: 0.14, extraRobo: 0.014 },
  { name: 'scale', price: 900, aiMinutes: 7500, roboMinutes: 75000, extraAI: 0.12, extraRobo: 0.012 },
];

// ENIQ AI per-user prices (not affected by package tier, except Corporate
// which includes Standard). Anchored = Business Phone / FMC in the bundle.
export const ENIQ_ANCHORED_ADDON_PRICES = {
  standard: { ua: 21, eu: 21, other: 21, asia: 21 },
  pro: { ua: 34, eu: 34, other: 34, asia: 34 },
};

// Standalone Eniq (no Business Phone / FMC) is more expensive.
export const ENIQ_STANDALONE_ADDON_PRICES = {
  standard: { ua: 26, eu: 26, other: 26, asia: 26 },
  pro: { ua: 39, eu: 39, other: 39, asia: 39 },
};

// LEGACY per-minute plans (conserved) — see calculateEniqAIPrice
export const ENIQ_AI_PLANS = [
  { name: 'start', price: 140, credits: 2000, premiumMinutes: 2000, regularMinutes: 6000, extraPrice: 0.08 },
  { name: 'pro', price: 300, credits: 5000, premiumMinutes: 5000, regularMinutes: 15000, extraPrice: 0.07 },
  { name: 'max', price: 500, credits: 10000, premiumMinutes: 10000, regularMinutes: 30000, extraPrice: 0.06 },
  { name: 'scale', price: 900, credits: 20000, premiumMinutes: 20000, regularMinutes: 60000, extraPrice: 0.05 },
];

// ===============================================
// SCENARIO CATEGORIES
// ===============================================

export const SCENARIO_CATEGORIES = {
  'business-phone': 'Business Phone',
  'fmc': 'FMC',
  'ai-conversation': 'AI Conversation',
  'omnichannel-chat': 'Omnichannel Chat',
  'callback': 'Callback',
  'call-tracking': 'Call Tracking',
  'eniq-ai': 'Eniq AI',
  'voicebot': 'VoiceBot',
  'phone-numbers': 'Phone Numbers',
  'voice-traffic': 'Voice Traffic',
  'bundle': 'Bundle (Multi-product)',
};

export const TIERS = ['basic', 'pro', 'corporate', 'advanced'];
export const REGIONS = ['ua', 'eu', 'asia', 'other'];

// ===============================================
// HELPER FUNCTIONS
// ===============================================

export function round2(num) {
  return Math.round(num * 100) / 100;
}

export function calculatePackagePrice(users, tier, region) {
  const tierPrices = PACKAGE_TIER_PRICES[tier] || PACKAGE_TIER_PRICES.basic;
  const pricePerUser = tierPrices[region] || tierPrices.other;
  const minUsers = MIN_USERS[tier] || 3;
  const effectiveUsers = Math.max(users, minUsers);
  return effectiveUsers * pricePerUser;
}

export function calculateFMCPrice(users, tier, region, isStandalone = true) {
  // FMC is only available in Ukraine
  if (region !== 'ua') return 0;

  const pricePerUser = FMC_TIER_PRICES[tier] || FMC_TIER_PRICES.basic;
  const effectiveUsers = isStandalone ? Math.max(users, FMC_MIN_USERS_STANDALONE) : users;

  // No additional discount - just users × price
  return effectiveUsers * pricePerUser;
}

export function calculateAIConversationPrice(users, tier, region) {
  // Corporate includes Eniq AI Standard instead of AI Conversation Intelligence,
  // so AI is a paid add-on there, same as on Basic and Advanced.
  if (tier === 'pro') {
    return 0;
  }
  const addon = BASIC_TIER_ADDONS.ai_conversation;
  const pricePerUser = addon[region] || addon.other;
  return users * pricePerUser;
}

export function calculateOmnichannelChatPrice(users, tier, region) {
  if (tier === 'pro' || tier === 'corporate') {
    return 0;
  }
  const addon = BASIC_TIER_ADDONS.omnichannel_chat;
  const pricePerUser = addon[region] || addon.other;
  const effectiveUsers = Math.max(users, addon.minUsers);
  return effectiveUsers * pricePerUser;
}

export function calculateCallbackPrice(tier, region, hasBPBundle = false) {
  if (tier === 'pro' || tier === 'corporate' || tier === 'advanced') {
    return 0;
  }
  const addon = BASIC_TIER_ADDONS.callback;
  const basePrice = addon[region] || addon.other;

  // Apply bundle discount when Business Phone is present
  if (hasBPBundle) {
    const discount = getBundleDiscount(region);
    return Math.max(0, basePrice - discount);
  }

  return basePrice;
}

export function applySubscriptionDiscount(price, isEligible, subscriptionDays = 360) {
  if (!isEligible || price === 0) return price;
  const bonus = subscriptionDays === 360 ? SUBSCRIPTION_360_BONUS :
                subscriptionDays === 180 ? SUBSCRIPTION_180_BONUS : 0;
  return price / (1 + bonus);
}

export function getBundleDiscount(region) {
  return BUNDLE_DISCOUNT_AMOUNT[region] || BUNDLE_DISCOUNT_AMOUNT.other;
}

// QSLN Formula (Poisson distribution for telephone lines)
// Extracted from CalculatorContext as a pure function
export function calculateQSLN(traf) {
  if (traf <= 0) return 0;
  const traf_koef = Math.abs(traf) / 10 * 0.0833;
  if (traf_koef === 0 || isNaN(traf_koef)) return 0;

  let p0 = 1;
  let plast = 1;

  for (let i = 1; i < 10000; i++) {
    plast = plast * traf_koef / i;
    p0 = p0 + plast;
    if (i > 10000) break;
    if ((plast / p0) < 0.05) {
      const nums = (i > traf_koef * 1.05) ? i : Math.ceil(traf_koef * 1.05);
      return nums;
    }
  }
  return Math.ceil(traf_koef * 1.05);
}

/**
 * Calculate Call Tracking price (not affected by package tier)
 * Uses slots (QSLN output), not raw sessions
 */
export function calculateCallTrackingPrice(slots, region) {
  const component1 = 390000; // $39 in cents
  const component2 = 200000; // $20 in cents

  let variablePart = 0;
  if (slots > 5) {
    const extraSlots = slots - 5;
    const basePrice = extraSlots * 65000;

    let discountCoeff;
    if (slots <= 25) {
      const logBase = 2.5 + slots / 100;
      const logValue = Math.log(slots / 25) / Math.log(logBase);
      discountCoeff = Math.pow(0.9, logValue);
    } else {
      const logBase = 1 + slots / 120;
      const logValue = Math.log(slots / 25) / Math.log(logBase);
      discountCoeff = Math.pow(0.77, logValue);
    }

    variablePart = basePrice * discountCoeff;
  }

  let totalCents;
  if (region === 'ua') {
    totalCents = component1 * 0.7004 + component2 * 0.711 + variablePart * 0.715;
  } else {
    totalCents = component1 + component2 + variablePart;
  }

  return totalCents / 10000;
}

/**
 * Calculate Call Tracking price from sessions (convenience wrapper)
 * Converts sessions → QSLN slots → price
 */
export function calculateCallTrackingPriceFromSessions(sessions, region) {
  const slots = calculateQSLN(sessions);
  return calculateCallTrackingPrice(slots, region);
}

/**
 * Calculate ENIQ AI price with auto-plan selection
 * Same 15% optimization rule as backend
 */
/**
 * Minimum billed Eniq users in EVERY mode — mirrors the backend's
 * EniqMinBillableUsers. Applies standalone AND when the phone anchor
 * carries fewer than 5 users.
 */
export const ENIQ_MIN_BILLABLE_USERS = 5;

/**
 * Users a scenario bills Eniq for: Business Phone / FMC users when present,
 * otherwise the standalone slider count (`inputs.eniqUsers`) — but never
 * fewer than ENIQ_MIN_BILLABLE_USERS.
 */
export function getScenarioEniqUsers(inputs = {}, tier, products = []) {
  const hasUserBase = products.includes('business-phone') || products.includes('fmc');
  if (!hasUserBase) {
    return Math.max(inputs.eniqUsers ?? ENIQ_MIN_BILLABLE_USERS, ENIQ_MIN_BILLABLE_USERS);
  }

  let base;
  if (inputs.fmcUsers && !inputs.bpUsers) {
    base = Math.max(inputs.fmcUsers, FMC_MIN_USERS_STANDALONE);
  } else if (inputs.fmcUsers && inputs.bpUsers) {
    base = inputs.bpUsers + inputs.fmcUsers;
  } else {
    base = Math.max(inputs.bpUsers || 0, MIN_USERS[tier] || 3);
  }
  return Math.max(base, ENIQ_MIN_BILLABLE_USERS);
}

/**
 * Eniq AI per-user add-on price. Mirrors backend/services/eniq_pricing.go.
 * `hasPhoneAnchor` selects the anchored (21/34) vs standalone (26/39) table.
 */
export function calculateEniqAddonPrice(users, eniqTier, packageTier, region, hasPhoneAnchor = true) {
  const tier = eniqTier === 'pro' ? 'pro' : 'standard';
  const table = hasPhoneAnchor ? ENIQ_ANCHORED_ADDON_PRICES : ENIQ_STANDALONE_ADDON_PRICES;
  const priceFor = (t) => {
    const prices = table[t];
    return prices[region] !== undefined ? prices[region] : prices.other;
  };

  let pricePerUser = priceFor(tier);
  if (packageTier === 'corporate') {
    if (tier === 'standard') return 0;
    pricePerUser -= priceFor('standard');
  }

  return Math.max(0, users * pricePerUser);
}

/**
 * LEGACY per-minute Eniq AI pricing (conserved).
 * Kept in sync with backend/services/eniq_legacy_pricing.go so the conserved
 * model stays under test. Not used while ENIQ_PRICING_MODE is 'per_user'.
 */
export function calculateEniqAIPrice(minutes, premiumModels = false) {
  if (minutes <= 0) {
    return { price: 0, plan: 'none', includedMinutes: 0, extraMinutes: 0, extraCost: 0 };
  }

  const requiredCredits = premiumModels ? minutes : minutes * 0.3333;

  let bestPlan = null;
  let bestPrice = Infinity;
  let bestPlanDetails = null;

  for (const plan of ENIQ_AI_PLANS) {
    let totalCost;
    if (requiredCredits <= plan.credits) {
      totalCost = plan.price;
    } else {
      const extraCredits = requiredCredits - plan.credits;
      totalCost = plan.price + extraCredits * plan.extraPrice;
    }

    if (totalCost < bestPrice) {
      bestPrice = totalCost;
      bestPlan = plan.name;
      bestPlanDetails = plan;
    }
  }

  // Apply 15% optimization rule
  const currentPlanIndex = ENIQ_AI_PLANS.findIndex(p => p.name === bestPlan);
  if (currentPlanIndex < ENIQ_AI_PLANS.length - 1) {
    const nextPlan = ENIQ_AI_PLANS[currentPlanIndex + 1];
    let nextPlanCost;
    if (requiredCredits <= nextPlan.credits) {
      nextPlanCost = nextPlan.price;
    } else {
      nextPlanCost = nextPlan.price + (requiredCredits - nextPlan.credits) * nextPlan.extraPrice;
    }

    const percentageDiff = ((nextPlanCost - bestPrice) / bestPrice) * 100;
    if (percentageDiff <= 15 && percentageDiff >= 0) {
      bestPrice = nextPlanCost;
      bestPlan = nextPlan.name;
      bestPlanDetails = nextPlan;
    }
  }

  const includedMinutes = premiumModels ? bestPlanDetails.premiumMinutes : bestPlanDetails.regularMinutes;
  const extraMinutes = Math.max(0, minutes - includedMinutes);
  let extraCost = 0;
  if (extraMinutes > 0) {
    const extraCredits = premiumModels ? extraMinutes : extraMinutes * 0.3333;
    extraCost = extraCredits * bestPlanDetails.extraPrice;
  }

  return { price: bestPrice, plan: bestPlan, includedMinutes, extraMinutes, extraCost };
}

/**
 * Calculate VoiceBot price with auto-plan selection
 * Same algorithm as ENIQ AI: find cheapest plan, then 15% optimization rule
 */
export function calculateVoiceBotPrice(minutes, isAIMode = true, extraChannels = 0) {
  if (minutes <= 0) {
    return { price: 0, plan: 'none', includedMinutes: 0, extraMinutes: 0, extraCost: 0 };
  }

  const requiredCredits = isAIMode ? minutes * 1000 : minutes * 100;

  let bestPlan = null;
  let bestPrice = Infinity;
  let bestPlanDetails = null;

  for (const plan of VOICEBOT_PLANS) {
    let totalCost;
    if (requiredCredits <= plan.aiMinutes * 1000) {
      totalCost = plan.price;
    } else {
      const includedMin = isAIMode ? plan.aiMinutes : plan.roboMinutes;
      const extraMin = minutes - includedMin;
      const extraPrice = isAIMode ? plan.extraAI : plan.extraRobo;
      totalCost = extraMin > 0 ? plan.price + extraMin * extraPrice : plan.price;
    }

    if (totalCost < bestPrice) {
      bestPrice = totalCost;
      bestPlan = plan.name;
      bestPlanDetails = plan;
    }
  }

  // 15% optimization rule
  const currentIndex = VOICEBOT_PLANS.findIndex(p => p.name === bestPlan);
  if (currentIndex < VOICEBOT_PLANS.length - 1) {
    const nextPlan = VOICEBOT_PLANS[currentIndex + 1];
    let nextCost;
    const nextCredits = nextPlan.aiMinutes * 1000;
    if (requiredCredits <= nextCredits) {
      nextCost = nextPlan.price;
    } else {
      const includedMin = isAIMode ? nextPlan.aiMinutes : nextPlan.roboMinutes;
      const extraMin = minutes - includedMin;
      const extraPrice = isAIMode ? nextPlan.extraAI : nextPlan.extraRobo;
      nextCost = extraMin > 0 ? nextPlan.price + extraMin * extraPrice : nextPlan.price;
    }

    const percentageDiff = ((nextCost - bestPrice) / bestPrice) * 100;
    if (percentageDiff <= 15 && percentageDiff >= 0) {
      bestPrice = nextCost;
      bestPlan = nextPlan.name;
      bestPlanDetails = nextPlan;
    }
  }

  const includedMinutes = isAIMode ? bestPlanDetails.aiMinutes : bestPlanDetails.roboMinutes;
  const extraMinutes = Math.max(0, minutes - includedMinutes);
  const extraCost = extraMinutes > 0
    ? extraMinutes * (isAIMode ? bestPlanDetails.extraAI : bestPlanDetails.extraRobo)
    : 0;

  const channelsCost = extraChannels * 25;
  return { price: bestPrice + channelsCost, plan: bestPlan, includedMinutes, extraMinutes, extraCost };
}

// ===============================================
// SCENARIO TEMPLATES (Base definitions)
// ===============================================

/**
 * Base scenario templates - will be expanded to all regions
 * Each template defines:
 * - id: unique identifier (region will be appended)
 * - category: product category
 * - tier: package tier
 * - description: human readable description
 * - products: array of products included
 * - inputs: calculation inputs
 * - calculateExpected: function to calculate expected values for a region
 */

const SCENARIO_TEMPLATES = [
  // ========================================
  // SINGLE PRODUCT SCENARIOS (10)
  // ========================================

  // 1. Business Phone Basic - 5 users
  {
    id: 'bp-basic-5',
    category: 'business-phone',
    tier: 'basic',
    description: 'Business Phone Basic, 5 users',
    products: ['business-phone'],
    inputs: { bpUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      return { basePrice: bp, products: { bp } };
    },
  },

  // 2. Business Phone Pro - 10 users
  {
    id: 'bp-pro-10',
    category: 'business-phone',
    tier: 'pro',
    description: 'Business Phone Pro, 10 users',
    products: ['business-phone'],
    inputs: { bpUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      return { basePrice: bp, products: { bp } };
    },
  },

  // 3. Business Phone Corporate - 15 users
  {
    id: 'bp-corp-15',
    category: 'business-phone',
    tier: 'corporate',
    description: 'Business Phone Corporate, 15 users',
    products: ['business-phone'],
    inputs: { bpUsers: 15 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(15, 'corporate', region);
      return { basePrice: bp, products: { bp } };
    },
  },

  // 4. FMC Basic - 6 users (UA only)
  {
    id: 'fmc-basic-6',
    category: 'fmc',
    tier: 'basic',
    regions: ['ua'],
    description: 'FMC Basic, 6 users (UA only)',
    products: ['fmc'],
    inputs: { fmcUsers: 6 },
    calculateExpected: (region) => {
      const fmc = calculateFMCPrice(6, 'basic', region, true);
      return { basePrice: fmc, products: { fmc } };
    },
  },

  // 5. FMC Pro - 10 users (UA only)
  {
    id: 'fmc-pro-10',
    category: 'fmc',
    tier: 'pro',
    regions: ['ua'],
    description: 'FMC Pro, 10 users (UA only)',
    products: ['fmc'],
    inputs: { fmcUsers: 10 },
    calculateExpected: (region) => {
      const fmc = calculateFMCPrice(10, 'pro', region, true);
      return { basePrice: fmc, products: { fmc } };
    },
  },

  // 6. AI Conversation Basic - 5 users (add-on pricing)
  {
    id: 'ai-basic-5',
    category: 'ai-conversation',
    tier: 'basic',
    description: 'AI Conversation Basic add-on, 5 users',
    products: ['ai-conversation'],
    inputs: { aiUsers: 5 },
    calculateExpected: (region) => {
      const ai = calculateAIConversationPrice(5, 'basic', region);
      return { basePrice: ai, products: { ai } };
    },
  },

  // 7. Omnichannel Chat Basic - 5 users
  {
    id: 'chat-basic-5',
    category: 'omnichannel-chat',
    tier: 'basic',
    description: 'Omnichannel Chat Basic add-on, 5 users',
    products: ['omnichannel-chat'],
    inputs: { chatUsers: 5 },
    calculateExpected: (region) => {
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      return { basePrice: chat, products: { chat } };
    },
  },

  // 8. Callback Basic - flat rate
  {
    id: 'callback-basic',
    category: 'callback',
    tier: 'basic',
    description: 'Callback Basic add-on (flat rate)',
    products: ['callback'],
    inputs: {},
    calculateExpected: (region) => {
      const callback = calculateCallbackPrice('basic', region);
      return { basePrice: callback, products: { callback } };
    },
  },

  // 9. Business Phone Basic - minimum users (3)
  {
    id: 'bp-basic-min',
    category: 'business-phone',
    tier: 'basic',
    description: 'Business Phone Basic, 1 user (min 3 charged)',
    products: ['business-phone'],
    inputs: { bpUsers: 1 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(1, 'basic', region); // min 3
      return { basePrice: bp, products: { bp } };
    },
  },

  // 10. Business Phone Corporate - minimum users (10)
  {
    id: 'bp-corp-min',
    category: 'business-phone',
    tier: 'corporate',
    description: 'Business Phone Corporate, 5 users (min 10 charged)',
    products: ['business-phone'],
    inputs: { bpUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'corporate', region); // min 10
      return { basePrice: bp, products: { bp } };
    },
  },

  // ========================================
  // MULTI-PRODUCT SCENARIOS (20)
  // ========================================

  // 11. BP Basic + AI Basic
  {
    id: 'bundle-bp-ai-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + AI Basic, 5 users',
    products: ['business-phone', 'ai-conversation'],
    inputs: { bpUsers: 5, aiUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ai = calculateAIConversationPrice(5, 'basic', region);
      return { basePrice: bp + ai, products: { bp, ai } };
    },
  },

  // 12. BP Basic + Chat Basic
  {
    id: 'bundle-bp-chat-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + Chat Basic, 5 users',
    products: ['business-phone', 'omnichannel-chat'],
    inputs: { bpUsers: 5, chatUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      return { basePrice: bp + chat, products: { bp, chat } };
    },
  },

  // 13. BP Basic + Callback Basic
  {
    id: 'bundle-bp-callback-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + Callback Basic, 5 users',
    products: ['business-phone', 'callback'],
    inputs: { bpUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // hasBPBundle = true
      return { basePrice: bp + callback, products: { bp, callback } };
    },
  },

  // 14. BP Basic + AI + Chat
  {
    id: 'bundle-bp-ai-chat-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + AI + Chat, 5 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat'],
    inputs: { bpUsers: 5, aiUsers: 5, chatUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ai = calculateAIConversationPrice(5, 'basic', region);
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      return { basePrice: bp + ai + chat, products: { bp, ai, chat } };
    },
  },

  // 15. BP Basic + AI + Callback
  {
    id: 'bundle-bp-ai-callback-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + AI + Callback, 5 users',
    products: ['business-phone', 'ai-conversation', 'callback'],
    inputs: { bpUsers: 5, aiUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ai = calculateAIConversationPrice(5, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // hasBPBundle = true
      return { basePrice: bp + ai + callback, products: { bp, ai, callback } };
    },
  },

  // 16. BP Basic + Chat + Callback
  {
    id: 'bundle-bp-chat-callback-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic + Chat + Callback, 5 users',
    products: ['business-phone', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 5, chatUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // hasBPBundle = true
      return { basePrice: bp + chat + callback, products: { bp, chat, callback } };
    },
  },

  // 17. BP Basic + AI + Chat + Callback (Full Basic Bundle)
  {
    id: 'bundle-full-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'Full Basic Bundle (BP+AI+Chat+Callback), 5 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 5, aiUsers: 5, chatUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ai = calculateAIConversationPrice(5, 'basic', region);
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // hasBPBundle = true
      return { basePrice: bp + ai + chat + callback, products: { bp, ai, chat, callback } };
    },
  },

  // 18. BP Basic + FMC Basic (UA bundle)
  {
    id: 'bundle-bp-fmc-basic-5',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'BP Basic + FMC Basic, 5 users',
    products: ['business-phone', 'fmc'],
    inputs: { bpUsers: 5, fmcUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const fmc = calculateFMCPrice(5, 'basic', region, false); // combined, no min
      return { basePrice: bp + fmc, products: { bp, fmc } };
    },
  },

  // 19. BP Pro + AI (AI included = $0)
  {
    id: 'bundle-bp-ai-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro + AI (included), 10 users',
    products: ['business-phone', 'ai-conversation'],
    inputs: { bpUsers: 10, aiUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const ai = calculateAIConversationPrice(10, 'pro', region); // $0
      return { basePrice: bp + ai, products: { bp, ai } };
    },
  },

  // 20. BP Pro + Chat (Chat included = $0)
  {
    id: 'bundle-bp-chat-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro + Chat (included), 10 users',
    products: ['business-phone', 'omnichannel-chat'],
    inputs: { bpUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const chat = calculateOmnichannelChatPrice(10, 'pro', region); // $0
      return { basePrice: bp + chat, products: { bp, chat } };
    },
  },

  // 21. BP Pro + Callback (Callback included = $0)
  {
    id: 'bundle-bp-callback-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro + Callback (included), 10 users',
    products: ['business-phone', 'callback'],
    inputs: { bpUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const callback = calculateCallbackPrice('pro', region); // $0
      return { basePrice: bp + callback, products: { bp, callback } };
    },
  },

  // 22. BP Pro + AI + Chat (all included)
  {
    id: 'bundle-bp-ai-chat-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro + AI + Chat (all included), 10 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat'],
    inputs: { bpUsers: 10, aiUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const ai = calculateAIConversationPrice(10, 'pro', region);
      const chat = calculateOmnichannelChatPrice(10, 'pro', region);
      return { basePrice: bp + ai + chat, products: { bp, ai, chat } };
    },
  },

  // 23. Full Pro Bundle (BP + AI + Chat + Callback)
  {
    id: 'bundle-full-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'Full Pro Bundle (all included), 10 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 10, aiUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const ai = calculateAIConversationPrice(10, 'pro', region);
      const chat = calculateOmnichannelChatPrice(10, 'pro', region);
      const callback = calculateCallbackPrice('pro', region);
      return { basePrice: bp + ai + chat + callback, products: { bp, ai, chat, callback } };
    },
  },

  // 24. BP Pro + FMC Pro
  {
    id: 'bundle-bp-fmc-pro-10',
    category: 'bundle',
    tier: 'pro',
    regions: ['ua'],
    description: 'BP Pro + FMC Pro, 10 users',
    products: ['business-phone', 'fmc'],
    inputs: { bpUsers: 10, fmcUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const fmc = calculateFMCPrice(10, 'pro', region, false);
      return { basePrice: bp + fmc, products: { bp, fmc } };
    },
  },

  // 25. Full Corporate Bundle
  {
    id: 'bundle-full-corp-15',
    category: 'bundle',
    tier: 'corporate',
    description: 'Full Corporate Bundle (all included), 15 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 15, aiUsers: 15, chatUsers: 15 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(15, 'corporate', region);
      const ai = calculateAIConversationPrice(15, 'corporate', region);
      const chat = calculateOmnichannelChatPrice(15, 'corporate', region);
      const callback = calculateCallbackPrice('corporate', region);
      return { basePrice: bp + ai + chat + callback, products: { bp, ai, chat, callback } };
    },
  },

  // 26. BP Corporate + FMC Corporate
  {
    id: 'bundle-bp-fmc-corp-15',
    category: 'bundle',
    tier: 'corporate',
    regions: ['ua'],
    description: 'BP Corporate + FMC Corporate, 15 users',
    products: ['business-phone', 'fmc'],
    inputs: { bpUsers: 15, fmcUsers: 15 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(15, 'corporate', region);
      const fmc = calculateFMCPrice(15, 'corporate', region, false);
      return { basePrice: bp + fmc, products: { bp, fmc } };
    },
  },

  // 27. Full Basic Bundle - 10 users
  {
    id: 'bundle-full-basic-10',
    category: 'bundle',
    tier: 'basic',
    description: 'Full Basic Bundle, 10 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 10, aiUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'basic', region);
      const ai = calculateAIConversationPrice(10, 'basic', region);
      const chat = calculateOmnichannelChatPrice(10, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // hasBPBundle = true
      return { basePrice: bp + ai + chat + callback, products: { bp, ai, chat, callback } };
    },
  },

  // 28. BP Pro + FMC + Full addons
  {
    id: 'bundle-pro-fmc-full-10',
    category: 'bundle',
    tier: 'pro',
    regions: ['ua'],
    description: 'BP Pro + FMC + AI + Chat + Callback, 10 users',
    products: ['business-phone', 'fmc', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 10, fmcUsers: 10, aiUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const fmc = calculateFMCPrice(10, 'pro', region, false);
      const ai = calculateAIConversationPrice(10, 'pro', region);
      const chat = calculateOmnichannelChatPrice(10, 'pro', region);
      const callback = calculateCallbackPrice('pro', region);
      return { basePrice: bp + fmc + ai + chat + callback, products: { bp, fmc, ai, chat, callback } };
    },
  },

  // 29. Full Corporate + FMC
  {
    id: 'bundle-corp-fmc-full-20',
    category: 'bundle',
    tier: 'corporate',
    regions: ['ua'],
    description: 'Full Corporate + FMC, 20 users',
    products: ['business-phone', 'fmc', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 20, fmcUsers: 20, aiUsers: 20, chatUsers: 20 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(20, 'corporate', region);
      const fmc = calculateFMCPrice(20, 'corporate', region, false);
      const ai = calculateAIConversationPrice(20, 'corporate', region);
      const chat = calculateOmnichannelChatPrice(20, 'corporate', region);
      const callback = calculateCallbackPrice('corporate', region);
      return { basePrice: bp + fmc + ai + chat + callback, products: { bp, fmc, ai, chat, callback } };
    },
  },

  // 30. BP Basic + FMC + AI + Chat (no callback)
  {
    id: 'bundle-basic-fmc-partial-8',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'BP Basic + FMC + AI + Chat, 8 users',
    products: ['business-phone', 'fmc', 'ai-conversation', 'omnichannel-chat'],
    inputs: { bpUsers: 8, fmcUsers: 8, aiUsers: 8, chatUsers: 8 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(8, 'basic', region);
      const fmc = calculateFMCPrice(8, 'basic', region, false);
      const ai = calculateAIConversationPrice(8, 'basic', region);
      const chat = calculateOmnichannelChatPrice(8, 'basic', region);
      return { basePrice: bp + fmc + ai + chat, products: { bp, fmc, ai, chat } };
    },
  },

  // ========================================
  // ADVANCED TIER SCENARIOS (4)
  // ========================================

  // 31. Business Phone Advanced - 10 users (min 10, Pro prices)
  {
    id: 'bp-advanced-10',
    category: 'business-phone',
    tier: 'advanced',
    description: 'Business Phone Advanced, 10 users',
    products: ['business-phone'],
    inputs: { bpUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'advanced', region);
      return { basePrice: bp, products: { bp } };
    },
  },

  // 32. BP Advanced + AI add-on (paid at Basic prices)
  {
    id: 'bundle-bp-ai-advanced-10',
    category: 'bundle',
    tier: 'advanced',
    description: 'BP Advanced + AI (paid add-on), 10 users',
    products: ['business-phone', 'ai-conversation'],
    inputs: { bpUsers: 10, aiUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'advanced', region);
      const ai = calculateAIConversationPrice(10, 'advanced', region); // Basic add-on price
      return { basePrice: bp + ai, products: { bp, ai } };
    },
  },

  // 33. Full Advanced Bundle (BP + AI + Chat + Callback=free)
  {
    id: 'bundle-full-advanced-10',
    category: 'bundle',
    tier: 'advanced',
    description: 'Full Advanced Bundle (AI+Chat paid, Callback free), 10 users',
    products: ['business-phone', 'ai-conversation', 'omnichannel-chat', 'callback'],
    inputs: { bpUsers: 10, aiUsers: 10, chatUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'advanced', region);
      const ai = calculateAIConversationPrice(10, 'advanced', region); // Basic add-on price
      const chat = calculateOmnichannelChatPrice(10, 'advanced', region); // Basic add-on price
      const callback = calculateCallbackPrice('advanced', region); // $0 (included)
      return { basePrice: bp + ai + chat + callback, products: { bp, ai, chat, callback } };
    },
  },

  // 34. BP Advanced + FMC Advanced (UA only)
  {
    id: 'bundle-bp-fmc-advanced-10',
    category: 'bundle',
    tier: 'advanced',
    regions: ['ua'],
    description: 'BP Advanced + FMC Advanced, 10 users (UA only)',
    products: ['business-phone', 'fmc'],
    inputs: { bpUsers: 10, fmcUsers: 10 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'advanced', region);
      const fmc = calculateFMCPrice(10, 'advanced', region, false); // combined, no min
      return { basePrice: bp + fmc, products: { bp, fmc } };
    },
  },

  // ========================================
  // CALL TRACKING SCENARIOS (3)
  // ========================================

  // 35. CT ~100 sessions/day (dynamic, 1 format)
  {
    id: 'ct-100-sessions',
    category: 'call-tracking',
    tier: 'basic',
    description: 'Call Tracking ~100 sessions/day',
    products: ['call-tracking'],
    inputs: { ctSessions: 100 },
    calculateExpected: (region) => {
      const ct = calculateCallTrackingPriceFromSessions(100, region);
      return { basePrice: ct, products: { ct } };
    },
  },

  // 36. CT ~500 sessions/day
  {
    id: 'ct-500-sessions',
    category: 'call-tracking',
    tier: 'basic',
    description: 'Call Tracking ~500 sessions/day',
    products: ['call-tracking'],
    inputs: { ctSessions: 500 },
    calculateExpected: (region) => {
      const ct = calculateCallTrackingPriceFromSessions(500, region);
      return { basePrice: ct, products: { ct } };
    },
  },

  // 37. CT ~1000 sessions/day
  {
    id: 'ct-1000-sessions',
    category: 'call-tracking',
    tier: 'basic',
    description: 'Call Tracking ~1000 sessions/day',
    products: ['call-tracking'],
    inputs: { ctSessions: 1000 },
    calculateExpected: (region) => {
      const ct = calculateCallTrackingPriceFromSessions(1000, region);
      return { basePrice: ct, products: { ct } };
    },
  },

  // ========================================
  // ENIQ AI SCENARIOS (4)
  // ========================================

  // 38. Eniq AI Standard as an add-on on Basic
  {
    id: 'eniq-standard-basic',
    category: 'eniq-ai',
    tier: 'basic',
    description: 'BP Basic 5 + Eniq AI Standard (5 users)',
    products: ['business-phone', 'eniq-ai'],
    inputs: { bpUsers: 5, eniqTier: 'standard' },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const eniq = calculateEniqAddonPrice(5, 'standard', 'basic', region);
      // Eniq is not eligible for the subscription discount
      return {
        basePrice: bp + eniq,
        subscriptionEligiblePrice: bp,
        products: { bp, eniq },
      };
    },
  },

  // 39. Eniq AI Pro as an add-on on Basic
  {
    id: 'eniq-pro-basic',
    category: 'eniq-ai',
    tier: 'basic',
    description: 'BP Basic 5 + Eniq AI Pro (5 users)',
    products: ['business-phone', 'eniq-ai'],
    inputs: { bpUsers: 5, eniqTier: 'pro' },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const eniq = calculateEniqAddonPrice(5, 'pro', 'basic', region);
      return {
        basePrice: bp + eniq,
        subscriptionEligiblePrice: bp,
        products: { bp, eniq },
      };
    },
  },

  // 40. Eniq AI Standard included in Corporate
  {
    id: 'eniq-corp-included',
    category: 'eniq-ai',
    tier: 'corporate',
    description: 'BP Corporate 10 + Eniq AI Standard (included, $0)',
    products: ['business-phone', 'eniq-ai'],
    inputs: { bpUsers: 10, eniqTier: 'standard' },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'corporate', region);
      const eniq = calculateEniqAddonPrice(10, 'standard', 'corporate', region); // 0
      return {
        basePrice: bp + eniq,
        subscriptionEligiblePrice: bp,
        products: { bp, eniq },
      };
    },
  },

  // 41. Eniq AI Pro on Corporate — pay only the difference
  {
    id: 'eniq-corp-pro-upgrade',
    category: 'eniq-ai',
    tier: 'corporate',
    description: 'BP Corporate 10 + Eniq AI Pro (upgrade from included Standard)',
    products: ['business-phone', 'eniq-ai'],
    inputs: { bpUsers: 10, eniqTier: 'pro' },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'corporate', region);
      const eniq = calculateEniqAddonPrice(10, 'pro', 'corporate', region); // 10 * 13
      return {
        basePrice: bp + eniq,
        subscriptionEligiblePrice: bp,
        products: { bp, eniq },
      };
    },
  },

  // ========================================
  // VOICEBOT SCENARIOS (4)
  // ========================================

  // 41. VoiceBot 1100 AI min → Lite $170
  {
    id: 'vbot-1100-ai',
    category: 'voicebot',
    tier: 'basic',
    description: 'VoiceBot 1100 AI min (Lite plan)',
    products: ['voicebot'],
    inputs: { vbMinutes: 1100, vbAIMode: true },
    calculateExpected: () => {
      const vb = calculateVoiceBotPrice(1100, true);
      return { basePrice: vb.price, subscriptionEligiblePrice: 0, products: { vbot: vb.price } };
    },
  },

  // 42. VoiceBot 2000 AI min → Pro $280
  {
    id: 'vbot-2000-ai',
    category: 'voicebot',
    tier: 'basic',
    description: 'VoiceBot 2000 AI min (Pro plan)',
    products: ['voicebot'],
    inputs: { vbMinutes: 2000, vbAIMode: true },
    calculateExpected: () => {
      const vb = calculateVoiceBotPrice(2000, true);
      return { basePrice: vb.price, subscriptionEligiblePrice: 0, products: { vbot: vb.price } };
    },
  },

  // 43. VoiceBot 4000 AI min → Max $520
  {
    id: 'vbot-4000-ai',
    category: 'voicebot',
    tier: 'basic',
    description: 'VoiceBot 4000 AI min (Max plan)',
    products: ['voicebot'],
    inputs: { vbMinutes: 4000, vbAIMode: true },
    calculateExpected: () => {
      const vb = calculateVoiceBotPrice(4000, true);
      return { basePrice: vb.price, subscriptionEligiblePrice: 0, products: { vbot: vb.price } };
    },
  },

  // 44. VoiceBot 11000 RoboCall min → Lite $170
  {
    id: 'vbot-11000-robo',
    category: 'voicebot',
    tier: 'basic',
    description: 'VoiceBot 11000 RoboCall min (Lite plan)',
    products: ['voicebot'],
    inputs: { vbMinutes: 11000, vbAIMode: false },
    calculateExpected: () => {
      const vb = calculateVoiceBotPrice(11000, false);
      return { basePrice: vb.price, subscriptionEligiblePrice: 0, products: { vbot: vb.price } };
    },
  },

  // ========================================
  // MIXED BUNDLES WITH NEW PRODUCTS (2)
  // ========================================

  // 45. BP Basic 5 users + CT 500 sessions (with CT bundle discount)
  {
    id: 'bundle-bp-ct-basic-5',
    category: 'bundle',
    tier: 'basic',
    description: 'BP Basic 5 users + CT 500 sessions',
    products: ['business-phone', 'call-tracking'],
    inputs: { bpUsers: 5, ctSessions: 500 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ctFull = calculateCallTrackingPriceFromSessions(500, region);
      const bundleDiscount = getBundleDiscount(region);
      const ct = Math.max(0, ctFull - bundleDiscount);
      return { basePrice: bp + ct, products: { bp, ct } };
    },
  },

  // 46. BP Pro 10 users + VoiceBot Lite 1100 AI min
  {
    id: 'bundle-bp-vbot-pro-10',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro 10 users + VoiceBot Lite 1100 AI min',
    products: ['business-phone', 'voicebot'],
    inputs: { bpUsers: 10, vbMinutes: 1100, vbAIMode: true },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const vb = calculateVoiceBotPrice(1100, true);
      return {
        basePrice: bp + vb.price,
        subscriptionEligiblePrice: bp, // only BP is eligible
        products: { bp, vbot: vb.price },
      };
    },
  },

  // ========================================
  // BUG FIX REGRESSION SCENARIOS
  // ========================================

  // 47. BP Basic 5 + FMC 3 + AI addon: AI should use 8 users (5+3), not just 5
  {
    id: 'bundle-bp-fmc-ai-combined-users',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'BP Basic 5 + FMC 3 + AI: AI uses combined users (5+3=8)',
    products: ['business-phone', 'fmc', 'ai-conversation'],
    inputs: { bpUsers: 5, fmcUsers: 3, aiUsers: 8 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const fmc = calculateFMCPrice(3, 'basic', region, false);
      const ai = calculateAIConversationPrice(8, 'basic', region); // 8 = 5 BP + 3 FMC
      return { basePrice: bp + fmc + ai, products: { bp, fmc, ai } };
    },
  },

  // 48. BP Basic 5 + FMC 3 + Chat: Chat should use 8 users (5+3), not just 5
  {
    id: 'bundle-bp-fmc-chat-bp-users-only',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'BP Basic 5 + FMC 3 + Chat: Chat counts Business Phone users only (5)',
    products: ['business-phone', 'fmc', 'omnichannel-chat'],
    inputs: { bpUsers: 5, fmcUsers: 3, chatUsers: 5 },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const fmc = calculateFMCPrice(3, 'basic', region, false);
      // FMC users have no smartphone app, so they are never chat users — 5 BP only
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      return { basePrice: bp + fmc + chat, products: { bp, fmc, chat } };
    },
  },

  // 49. FMC Basic 6 + Callback: FMC should get bundle discount on callback (same as BP)
  {
    id: 'fmc-callback-bundle-discount',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'FMC Basic 6 + Callback: FMC gets bundle discount on callback',
    products: ['fmc', 'callback'],
    inputs: { fmcUsers: 6 },
    calculateExpected: (region) => {
      const fmc = calculateFMCPrice(6, 'basic', region, true);
      const callback = calculateCallbackPrice('basic', region, true); // FMC gets BP bundle discount
      return { basePrice: fmc + callback, products: { fmc, callback } };
    },
  },

  // ========================================
  // PHONE NUMBERS & VOICE TRAFFIC SCENARIOS
  // (Multi-product with frontend-only items)
  // ========================================

  // 50. Phone-only with activation (all regions)
  {
    id: 'phone-only-with-activation',
    category: 'phone-numbers',
    tier: 'basic',
    description: 'Phone Numbers only with activation, 5 lines',
    products: ['phone-numbers'],
    inputs: {
      phoneNumbers: [
        { name: 'Main DID', quantity: 3, price: 4, activationFee: 10 },
        { name: 'Sales DID', quantity: 2, price: 7.5, activationFee: 0 },
      ],
      enablePhoneActivation: true,
    },
    calculateExpected: () => {
      const frontendOnly = 3 * 4 + 2 * 7.5; // 27
      const oneTime = 3 * 10; // 30
      return {
        basePrice: frontendOnly,
        backendBasePrice: 0,
        frontendOnlyMonthly: frontendOnly,
        oneTime,
        subscriptionEligiblePrice: 0, // phones not eligible
        products: { phoneNumbers: frontendOnly },
      };
    },
  },

  // 51. BP Basic + CT + Phone + Voice (basic, UA & Asia)
  {
    id: 'bundle-basic-bp-ct-phone-voice',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua', 'asia'],
    description: 'BP Basic 5 + CT 600 + Phone Numbers + Voice Traffic packages',
    products: ['business-phone', 'call-tracking', 'phone-numbers', 'voice-traffic'],
    inputs: {
      bpUsers: 5,
      ctSessions: 600,
      phoneNumbers: [
        { name: 'Main DID', quantity: 3, price: 4, activationFee: 10 },
        { name: 'Sales DID', quantity: 2, price: 7.5, activationFee: 0 },
      ],
      enablePhoneActivation: true,
      voiceTraffic: { mode: 'packages', countriesMinutes: { UA: 500, KZ: 1200 } },
    },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const ctFull = calculateCallTrackingPriceFromSessions(600, region);
      const bundleDiscount = getBundleDiscount(region);
      const ct = Math.max(0, ctFull - bundleDiscount);
      const phoneNumbers = 3 * 4 + 2 * 7.5; // 27
      const oneTime = 3 * 10; // 30
      const backendBase = bp + ct;
      return {
        basePrice: backendBase + phoneNumbers,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: phoneNumbers,
        oneTime,
        subscriptionEligiblePrice: backendBase, // BP+CT eligible, phones not
        skipApiValidation: true, // voice-traffic price not predictable in JS
        products: { bp, ct, phoneNumbers },
      };
    },
  },

  // 52. BP Pro + CT + Callback + Phone + Voice (pro, all regions)
  {
    id: 'bundle-pro-bp-ct-callback-phone-voice',
    category: 'bundle',
    tier: 'pro',
    description: 'BP Pro 10 + CT 1000 + Callback (incl) + Phone + Voice',
    products: ['business-phone', 'call-tracking', 'callback', 'phone-numbers', 'voice-traffic'],
    inputs: {
      bpUsers: 10,
      ctSessions: 1000,
      phoneNumbers: [
        { name: 'HQ DID', quantity: 2, price: 6, activationFee: 5 },
      ],
      enablePhoneActivation: true,
      voiceTraffic: { mode: 'packages', countriesMinutes: { UA: 1000 } },
    },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'pro', region);
      const ctFull = calculateCallTrackingPriceFromSessions(1000, region);
      const bundleDiscount = getBundleDiscount(region);
      const ct = Math.max(0, ctFull - bundleDiscount);
      const callback = calculateCallbackPrice('pro', region); // 0
      const phoneNumbers = 2 * 6; // 12
      const oneTime = 2 * 5; // 10
      const backendBase = bp + ct + callback;
      return {
        basePrice: backendBase + phoneNumbers,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: phoneNumbers,
        oneTime,
        subscriptionEligiblePrice: backendBase,
        skipApiValidation: true,
        products: { bp, ct, callback, phoneNumbers },
      };
    },
  },

  // 53. BP Corporate + Chat (free) + Eniq + Phone (all regions)
  {
    id: 'bundle-corp-bp-chat-eniq-phone',
    category: 'bundle',
    tier: 'corporate',
    description: 'BP Corp 15 + Chat (incl) + Eniq AI Standard (incl) + Phone Numbers',
    products: ['business-phone', 'omnichannel-chat', 'eniq-ai', 'phone-numbers'],
    inputs: {
      bpUsers: 15,
      chatUsers: 15,
      eniqTier: 'standard',
      phoneNumbers: [
        { name: 'Corp DID', quantity: 5, price: 8, activationFee: 12 },
      ],
      enablePhoneActivation: false,
    },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(15, 'corporate', region);
      const chat = calculateOmnichannelChatPrice(15, 'corporate', region); // 0
      const eniq = calculateEniqAddonPrice(15, 'standard', 'corporate', region); // included, 0
      const phoneNumbers = 5 * 8; // 40
      const backendBase = bp + chat + eniq;
      // eniq not eligible for subscription discount
      const eligible = bp + chat;
      return {
        basePrice: backendBase + phoneNumbers,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: phoneNumbers,
        oneTime: 0,
        subscriptionEligiblePrice: eligible,
        products: { bp, chat, eniq, phoneNumbers },
      };
    },
  },

  // 54. BP Advanced + Callback (free) + VoiceBot + Voice (ua, eu)
  {
    id: 'bundle-advanced-bp-callback-vbot-voice',
    category: 'bundle',
    tier: 'advanced',
    regions: ['ua', 'eu'],
    description: 'BP Advanced 10 + Callback (incl) + VoiceBot Lite + Voice',
    products: ['business-phone', 'callback', 'voicebot', 'voice-traffic'],
    inputs: {
      bpUsers: 10,
      vbMinutes: 1100,
      vbAIMode: true,
      voiceTraffic: { mode: 'packages', countriesMinutes: { UA: 800 } },
    },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(10, 'advanced', region);
      const callback = calculateCallbackPrice('advanced', region); // 0
      const vb = calculateVoiceBotPrice(1100, true);
      const backendBase = bp + callback + vb.price;
      const eligible = bp + callback;
      return {
        basePrice: backendBase,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: 0,
        oneTime: 0,
        subscriptionEligiblePrice: eligible,
        skipApiValidation: true, // voice-traffic price unpredictable
        products: { bp, callback, vbot: vb.price },
      };
    },
  },

  // 55. FMC + AI + Callback + Phone (UA only)
  {
    id: 'bundle-fmc-ai-callback-phone',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'FMC Basic 6 + AI + Callback + Phone Numbers (UA only)',
    products: ['fmc', 'ai-conversation', 'callback', 'phone-numbers'],
    inputs: {
      fmcUsers: 6,
      aiUsers: 6,
      phoneNumbers: [
        { name: 'UA mobile', quantity: 2, price: 4, activationFee: 10 },
      ],
      enablePhoneActivation: true,
    },
    calculateExpected: (region) => {
      const fmc = calculateFMCPrice(6, 'basic', region, true);
      const ai = calculateAIConversationPrice(6, 'basic', region);
      const callback = calculateCallbackPrice('basic', region, true); // FMC gets bundle discount
      const phoneNumbers = 2 * 4; // 8
      const oneTime = 2 * 10; // 20
      const backendBase = fmc + ai + callback;
      return {
        basePrice: backendBase + phoneNumbers,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: phoneNumbers,
        oneTime,
        subscriptionEligiblePrice: backendBase,
        products: { fmc, ai, callback, phoneNumbers },
      };
    },
  },

  // 56. BP + FMC + CT + Chat + Phone (UA only)
  {
    id: 'bundle-bp-fmc-ct-chat-phone',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua'],
    description: 'BP Basic 5 + FMC 3 + CT 500 + Chat (BP users only) + Phone (UA only)',
    products: ['business-phone', 'fmc', 'call-tracking', 'omnichannel-chat', 'phone-numbers'],
    inputs: {
      bpUsers: 5,
      fmcUsers: 3,
      ctSessions: 500,
      chatUsers: 5, // BP users only — FMC users cannot use chat
      phoneNumbers: [
        { name: 'Office DID', quantity: 3, price: 5, activationFee: 7 },
      ],
      enablePhoneActivation: true,
    },
    calculateExpected: (region) => {
      const bp = calculatePackagePrice(5, 'basic', region);
      const fmc = calculateFMCPrice(3, 'basic', region, false);
      const ctFull = calculateCallTrackingPriceFromSessions(500, region);
      const bundleDiscount = getBundleDiscount(region);
      const ct = Math.max(0, ctFull - bundleDiscount);
      const chat = calculateOmnichannelChatPrice(5, 'basic', region);
      const phoneNumbers = 3 * 5; // 15
      const oneTime = 3 * 7; // 21
      const backendBase = bp + fmc + ct + chat;
      return {
        basePrice: backendBase + phoneNumbers,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: phoneNumbers,
        oneTime,
        subscriptionEligiblePrice: backendBase,
        products: { bp, fmc, ct, chat, phoneNumbers },
      };
    },
  },

  // 57. Voice products stack — Eniq + VoiceBot + Voice Traffic
  {
    id: 'bundle-voice-products-stack',
    category: 'bundle',
    tier: 'basic',
    regions: ['ua', 'eu'],
    description: 'Eniq AI + VoiceBot + Voice Traffic packages stack',
    products: ['eniq-ai', 'voicebot', 'voice-traffic'],
    inputs: {
      eniqTier: 'standard',
      vbMinutes: 1100,
      vbAIMode: true,
      voiceTraffic: { mode: 'packages', countriesMinutes: { UA: 500 } },
    },
    calculateExpected: (region) => {
      // No Business Phone here — Eniq is sold standalone at the 5-user
      // minimum and at the standalone price (26/user for Standard).
      const eniq = calculateEniqAddonPrice(ENIQ_MIN_BILLABLE_USERS, 'standard', 'basic', region, false);
      const vb = calculateVoiceBotPrice(1100, true);
      const backendBase = eniq + vb.price;
      return {
        basePrice: backendBase,
        backendBasePrice: backendBase,
        frontendOnlyMonthly: 0,
        oneTime: 0,
        subscriptionEligiblePrice: 0, // eniq, vbot, voice not eligible
        skipApiValidation: true,
        products: { eniq, vbot: vb.price },
      };
    },
  },

  // 57b. Standalone Eniq with a non-default slider user count
  {
    id: 'eniq-standalone-custom-users',
    category: 'eniq-ai',
    tier: 'basic',
    regions: ['ua', 'eu'],
    description: 'Standalone Eniq AI Standard, slider at 12 users (no phone anchor)',
    products: ['eniq-ai'],
    inputs: { eniqTier: 'standard', eniqUsers: 12 },
    calculateExpected: (region) => {
      // No Business Phone / FMC — the standalone slider drives billing:
      // 12 users at the standalone price (26/user for Standard) = 312.
      const eniq = calculateEniqAddonPrice(
        getScenarioEniqUsers({ eniqUsers: 12 }, 'basic', ['eniq-ai']),
        'standard',
        'basic',
        region,
        false
      );
      return {
        basePrice: eniq,
        backendBasePrice: eniq,
        frontendOnlyMonthly: 0,
        oneTime: 0,
        subscriptionEligiblePrice: 0, // eniq not eligible
        products: { eniq },
      };
    },
  },

  // 58. Voice Traffic custom deposit only (EU)
  {
    id: 'voice-traffic-custom-deposit-eu',
    category: 'voice-traffic',
    tier: 'basic',
    regions: ['eu'],
    description: 'Voice Traffic custom deposit €250 (EU only)',
    products: ['voice-traffic'],
    inputs: {
      voiceTraffic: { mode: 'custom', customAmount: 250 },
    },
    calculateExpected: () => {
      // Backend honours custom_amount exactly, so we can predict the total deterministically.
      const customAmount = 250;
      return {
        basePrice: customAmount,
        backendBasePrice: customAmount,
        frontendOnlyMonthly: 0,
        oneTime: 0,
        subscriptionEligiblePrice: 0, // voice-traffic not eligible
        skipApiValidation: false,
        products: { voiceTraffic: customAmount },
      };
    },
  },
];

// ===============================================
// PRODUCT SHORT NAMES FOR UI
// ===============================================

export const PRODUCT_SHORT_NAMES = {
  'business-phone': 'BP',
  'fmc': 'FMC',
  'ai-conversation': 'AI',
  'omnichannel-chat': 'Chat',
  'callback': 'CB',
  'call-tracking': 'CT',
  'eniq-ai': 'Eniq',
  'voicebot': 'VBot',
  'phone-numbers': 'Phones',
  'voice-traffic': 'Voice',
};

/**
 * Generate productsList array for UI chips display
 * Each entry: { id, shortName, quantity }
 */
function generateProductsList(template) {
  const { products, inputs, tier } = template;

  return products.map(productId => {
    let quantity = null;

    switch (productId) {
      case 'business-phone':
        quantity = inputs.bpUsers || 0;
        break;
      case 'fmc':
        quantity = inputs.fmcUsers || 0;
        break;
      case 'ai-conversation':
        quantity = inputs.aiUsers || 0;
        break;
      case 'omnichannel-chat':
        quantity = inputs.chatUsers || 0;
        break;
      case 'callback':
        quantity = null; // flat rate, no quantity
        break;
      case 'call-tracking':
        quantity = inputs.ctSessions || 0;
        break;
      case 'eniq-ai':
        quantity = getScenarioEniqUsers(inputs, tier, products);
        break;
      case 'voicebot':
        quantity = inputs.vbMinutes || 0;
        break;
      case 'phone-numbers': {
        const phones = Array.isArray(inputs.phoneNumbers) ? inputs.phoneNumbers : [];
        quantity = phones.reduce((sum, p) => sum + Number(p.quantity || 0), 0) || null;
        break;
      }
      case 'voice-traffic':
        quantity = null; // varies (packages vs custom deposit)
        break;
      default:
        quantity = null;
    }

    return {
      id: productId,
      shortName: PRODUCT_SHORT_NAMES[productId] || productId,
      quantity,
    };
  });
}

// ===============================================
// GENERATE SCENARIOS FOR ALL REGIONS
// ===============================================

function generateScenarioForRegion(template, region) {
  const expected = template.calculateExpected(region);
  const regionLabel = region.toUpperCase();

  // Support subscriptionEligiblePrice for products that are NOT eligible for subscription discount
  // (e.g. VoiceBot, Eniq AI). Fallback to basePrice = 100% eligible (existing templates).
  const eligiblePrice = expected.subscriptionEligiblePrice ?? expected.basePrice;
  const ineligiblePrice = expected.basePrice - eligiblePrice;

  return {
    id: `${template.id}-${region}`,
    category: template.category,
    tier: template.tier,
    region: region,
    description: `${template.description} [${regionLabel}]`,
    products: template.products,
    productsList: generateProductsList(template),
    inputs: {
      ...template.inputs,
      tier: template.tier,
      region: region,
    },
    expected: {
      basePrice: round2(expected.basePrice),
      backendBasePrice: round2(expected.backendBasePrice ?? expected.basePrice),
      frontendOnlyMonthly: round2(expected.frontendOnlyMonthly ?? 0),
      oneTime: round2(expected.oneTime ?? 0),
      price360: round2(applySubscriptionDiscount(eligiblePrice, true, 360) + ineligiblePrice),
      price180: round2(applySubscriptionDiscount(eligiblePrice, true, 180) + ineligiblePrice),
      skipApiValidation: Boolean(expected.skipApiValidation),
      products: expected.products,
    },
    calculatorConfig: {
      packageTier: template.tier,
      businessPhoneUsers: template.inputs.bpUsers || 0,
      fmcUsers: template.inputs.fmcUsers || 0,
      aiUsers: template.inputs.aiUsers || 0,
      chatUsers: template.inputs.chatUsers || 0,
      ctSessions: template.inputs.ctSessions || 0,
      eniqMinutes: template.inputs.eniqMinutes || 0,
      eniqTier: template.inputs.eniqTier || 'standard',
      vbMinutes: template.inputs.vbMinutes || 0,
      selectedProducts: template.products,
      region: region,
      phoneNumbers: template.inputs.phoneNumbers || [],
      enablePhoneActivation: Boolean(template.inputs.enablePhoneActivation),
      voiceTraffic: template.inputs.voiceTraffic || null,
    },
  };
}

// Generate all scenarios (templates × regions, FMC-only templates restricted to UA)
export const PRICING_SCENARIOS = SCENARIO_TEMPLATES.flatMap(template =>
  (template.regions || REGIONS).map(region => generateScenarioForRegion(template, region))
);

// ===============================================
// HELPER FUNCTIONS FOR TEST DASHBOARD
// ===============================================

/**
 * Get scenarios filtered by category, tier, and/or region
 */
export function getFilteredScenarios({ category, tier, region } = {}) {
  return PRICING_SCENARIOS.filter(scenario => {
    if (category && scenario.category !== category) return false;
    if (tier && scenario.tier !== tier) return false;
    if (region && scenario.region !== region) return false;
    return true;
  });
}

/**
 * Calculate actual price for a scenario using the calculation functions
 */
export function calculateActualPrice(scenario) {
  const { inputs, products } = scenario;
  const { tier, region } = inputs;

  let total = 0;
  const productPrices = {};

  if (products.includes('business-phone')) {
    productPrices.bp = calculatePackagePrice(inputs.bpUsers || 0, tier, region);
    total += productPrices.bp;
  }

  if (products.includes('fmc')) {
    productPrices.fmc = calculateFMCPrice(inputs.fmcUsers || 0, tier, region, false);
    total += productPrices.fmc;
  }

  if (products.includes('ai-conversation')) {
    productPrices.ai = calculateAIConversationPrice(inputs.aiUsers || inputs.bpUsers || 0, tier, region);
    total += productPrices.ai;
  }

  if (products.includes('omnichannel-chat')) {
    productPrices.chat = calculateOmnichannelChatPrice(inputs.chatUsers || inputs.bpUsers || 0, tier, region);
    total += productPrices.chat;
  }

  if (products.includes('callback')) {
    // Apply bundle discount when Business Phone or FMC is present
    const hasBPBundle = products.includes('business-phone') || products.includes('fmc');
    productPrices.callback = calculateCallbackPrice(tier, region, hasBPBundle);
    total += productPrices.callback;
  }

  if (products.includes('call-tracking')) {
    const sessions = inputs.ctSessions || 100;
    productPrices.ct = calculateCallTrackingPriceFromSessions(sessions, region);
    total += productPrices.ct;
  }

  if (products.includes('eniq-ai')) {
    const eniqUsers = getScenarioEniqUsers(inputs, tier, products);
    const hasPhoneAnchor = products.includes('business-phone') || products.includes('fmc');
    productPrices.eniq = calculateEniqAddonPrice(eniqUsers, inputs.eniqTier, tier, region, hasPhoneAnchor);
    total += productPrices.eniq;
  }

  if (products.includes('voicebot')) {
    const vbResult = calculateVoiceBotPrice(inputs.vbMinutes || 0, inputs.vbAIMode !== false, inputs.vbExtraChannels || 0);
    productPrices.vbot = vbResult.price;
    total += productPrices.vbot;
  }

  if (products.includes('phone-numbers')) {
    const phoneNumbers = Array.isArray(inputs.phoneNumbers) ? inputs.phoneNumbers : [];
    productPrices.phoneNumbers = calculatePhoneNumbersMonthlyTotal(phoneNumbers);
    total += productPrices.phoneNumbers;
  }

  // Voice Traffic — only custom_amount is deterministic in JS.
  // Package mode pricing depends on backend rates, so we leave it to API validation.
  if (products.includes('voice-traffic')) {
    const voiceTraffic = inputs.voiceTraffic || {};
    if (voiceTraffic.mode === 'custom') {
      const amount = Number(voiceTraffic.customAmount || 0);
      productPrices.voiceTraffic = amount;
      total += amount;
    }
  }

  return { total: round2(total), products: productPrices };
}

/**
 * Calculate frontend-only monthly total (phone-numbers, etc.)
 * These products are not part of backend bundle pricing
 */
export function calculateScenarioFrontendOnlyMonthly(scenario) {
  const { inputs, products } = scenario;
  if (!products) return 0;
  let total = 0;
  if (products.includes('phone-numbers')) {
    const phoneNumbers = Array.isArray(inputs?.phoneNumbers) ? inputs.phoneNumbers : [];
    total += calculatePhoneNumbersMonthlyTotal(phoneNumbers);
  }
  return round2(total);
}

/**
 * Calculate scenario one-time total (e.g. phone activation fees)
 */
export function calculateScenarioOneTimeTotal(scenario) {
  const { inputs, products } = scenario;
  if (!products) return 0;
  let total = 0;
  if (products.includes('phone-numbers')) {
    total += calculatePhoneNumbersOneTimeTotal({
      enablePhoneActivation: Boolean(inputs?.enablePhoneActivation),
      phoneNumbers: Array.isArray(inputs?.phoneNumbers) ? inputs.phoneNumbers : [],
    });
  }
  return round2(total);
}

/**
 * Validate a scenario against actual calculation
 */
export function validateScenario(scenario) {
  const actual = calculateActualPrice(scenario);
  const expected = scenario.expected;

  const passed = Math.abs(actual.total - expected.basePrice) < 0.02;

  return {
    passed,
    actual: actual.total,
    expected: expected.basePrice,
    details: actual.products,
  };
}

/**
 * Get all scenarios with validation results
 */
export function getAllScenariosWithResults() {
  return PRICING_SCENARIOS.map(scenario => ({
    ...scenario,
    validation: validateScenario(scenario),
  }));
}

/**
 * Get statistics about scenarios
 */
export function getScenarioStats() {
  const results = getAllScenariosWithResults();
  const total = results.length;
  const passed = results.filter(s => s.validation.passed).length;
  const failed = total - passed;

  const byRegion = REGIONS.reduce((acc, region) => {
    const regionResults = results.filter(s => s.region === region);
    acc[region] = {
      total: regionResults.length,
      passed: regionResults.filter(s => s.validation.passed).length,
    };
    return acc;
  }, {});

  const byCategory = Object.keys(SCENARIO_CATEGORIES).reduce((acc, category) => {
    const categoryResults = results.filter(s => s.category === category);
    acc[category] = {
      total: categoryResults.length,
      passed: categoryResults.filter(s => s.validation.passed).length,
    };
    return acc;
  }, {});

  return { total, passed, failed, byRegion, byCategory };
}

// ===============================================
// BACKEND API VALIDATION (Real pricing engine)
// ===============================================

/**
 * Convert a scenario to backend API bundle request format
 * Maps frontend scenario structure to backend expected format
 */
export function scenarioToBundleRequest(scenario) {
  const { inputs, tier, region, products } = scenario;
  const bundleProducts = [];

  // Business Phone
  if (products.includes('business-phone')) {
    const minUsers = MIN_USERS[tier] || 3;
    const bpUsers = inputs.bpUsers || 3;
    bundleProducts.push({
      product_id: 'business_phone',
      region,
      quantity: Math.max(bpUsers, minUsers),
      package_tier: tier,
      business_phone_type: 'business-phone',
    });
  }

  // FMC (Ukraine only)
  if (products.includes('fmc')) {
    const fmcUsers = inputs.fmcUsers || 6;
    const isCombined = products.includes('business-phone');
    // FMC is only valid for UA region
    if (region === 'ua') {
      bundleProducts.push({
        product_id: 'fmc',
        region,
        quantity: isCombined ? fmcUsers : Math.max(fmcUsers, 6),
        package_tier: tier,
        business_phone_type: isCombined ? 'business-phone-fmc' : 'fmc',
      });
    }
  }

  // AI Conversation
  if (products.includes('ai-conversation')) {
    const aiUsers = inputs.aiUsers || inputs.bpUsers || 3;
    bundleProducts.push({
      product_id: 'ai_conversation',
      region,
      quantity: aiUsers,
      package_tier: tier,
    });
  }

  // Omnichannel Chat
  if (products.includes('omnichannel-chat')) {
    const chatUsers = inputs.chatUsers || inputs.bpUsers || 3;
    bundleProducts.push({
      product_id: 'omnichannel_chat',
      region,
      quantity: chatUsers,
      package_tier: tier,
    });
  }

  // Callback
  if (products.includes('callback')) {
    bundleProducts.push({
      product_id: 'callback',
      region,
      quantity: 100, // Default sessions
      package_tier: tier,
    });
  }

  // Call Tracking
  if (products.includes('call-tracking')) {
    const sessions = inputs.ctSessions || 100;
    const slots = calculateQSLN(sessions);
    bundleProducts.push({
      product_id: 'call_tracking',
      region,
      quantity: slots,
      package_tier: tier,
    });
  }

  // Eniq AI
  if (products.includes('eniq-ai')) {
    bundleProducts.push({
      product_id: 'eniq_ai',
      region,
      quantity: getScenarioEniqUsers(inputs, tier, products),
      package_tier: tier || '',
      eniq_tier: inputs.eniqTier || 'standard',
    });
  }

  // Voice Traffic (packages or custom deposit)
  if (products.includes('voice-traffic')) {
    const voiceTraffic = inputs.voiceTraffic || {};
    const product = {
      product_id: 'voice_traffic',
      region,
      quantity: 0,
      package_tier: tier,
    };

    if (voiceTraffic.mode === 'custom') {
      product.use_custom_amount = true;
      product.custom_amount = Number(voiceTraffic.customAmount || 0);
    } else {
      product.use_custom_amount = false;
      product.voice_traffic_countries_minutes = voiceTraffic.countriesMinutes
        || { UA: inputs.voiceTrafficMinutes || 500 };
    }

    bundleProducts.push(product);
  }

  // VoiceBot
  if (products.includes('voicebot')) {
    const minutes = inputs.vbMinutes || 0;
    const isAIMode = inputs.vbAIMode !== false;
    const extraChannels = inputs.vbExtraChannels || 0;
    bundleProducts.push({
      product_id: 'voicebot',
      region,
      quantity: minutes,
      premium_models: isAIMode,
      extra_channels: extraChannels,
    });
  }

  return {
    region,
    products: bundleProducts,
  };
}

/**
 * Get all scenarios (without async validation - for initial render)
 * Uses JS formulas for quick display, then API results can be fetched separately
 */
export function getAllScenariosForDisplay() {
  return PRICING_SCENARIOS.map(scenario => ({
    ...scenario,
    validation: null, // Will be populated by async API call
  }));
}

export default PRICING_SCENARIOS;
